import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContactReportSummary } from '@nemo/api';
import { useTRPC } from '../trpc.js';
import { Select } from '../ui/Select.js';
import { formatDay } from '../ui/format.js';
import { useToast } from '../ui/Toast.js';
import { CollapsibleCard } from './CollapsibleCard.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

export interface ReportedContactsProps {
  /** What `enrichment.status` counted, so the heading is right before the list loads. */
  needsReviewCount: number | undefined;
}

/**
 * The contacts people reported that nobody could place: a misspelt name, a
 * fund written as something the list does not use, two partners it could
 * equally have been.
 *
 * Nothing is silently created from an unmatched name, which is why this card
 * exists. Each row offers the three answers — it is this person, it is
 * somebody we do not hold yet, or it is not worth keeping — and a matched
 * decision does exactly what a clean match would have done.
 */
export function ReportedContacts(props: ReportedContactsProps): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();

  const reports = useQuery(trpc.contacts.list.queryOptions({ status: 'needs_review' }));

  const decide = useMutation(
    trpc.contacts.decide.mutationOptions({
      onSuccess: (decided) => {
        void queryClient.invalidateQueries({ queryKey: trpc.contacts.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.enrichment.status.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.investors.list.queryKey() });
        invalidateFundRows(queryClient, trpc);
        toast(
          decided.status === 'ignored'
            ? 'Ignored.'
            : `Matched to ${decided.investorName ?? ''} at ${decided.fundName ?? ''}.`,
        );
      },
      onError: (error: { message: string }) => toast(error.message),
    }),
  );

  const waiting = reports.data?.length ?? props.needsReviewCount ?? 0;

  return (
    <CollapsibleCard id="reported-contacts" title="Reported contacts" count={waiting}>
      <p className="subtle">{waitingPhrase(waiting)}</p>

      {(reports.data ?? []).map((report) => (
        <ReviewRow
          key={report.id}
          report={report}
          busy={decide.isPending}
          onDecide={(decision) => decide.mutate({ reportId: report.id, decision })}
        />
      ))}
    </CollapsibleCard>
  );
}

interface ReviewRowProps {
  report: ContactReportSummary;
  busy: boolean;
  onDecide: (
    decision: { kind: 'match'; investorId: string } | { kind: 'createPerson' } | { kind: 'ignore' },
  ) => void;
}

/** The picker's own value while nobody has chosen anybody. */
const NOBODY = '';

function ReviewRow(props: ReviewRowProps): React.JSX.Element {
  const { report } = props;
  const [picked, setPicked] = useState(NOBODY);

  return (
    <div className="review-row">
      <p className="review-said">
        <strong>{report.personName}</strong> at {report.fundNameReported} ·{' '}
        {formatDay(report.happenedAt)} · {report.channel}
      </p>
      <p className="subtle">
        {report.memberName} reported it{report.intel === null ? '' : `: ${report.intel}`}
      </p>

      {report.fundId === null ? (
        // Nothing to pick from: the fund itself could not be placed, so there
        // is no list of people this could have been.
        <p className="subtle">
          No fund here reads as “{report.fundNameReported}”, so there is nobody to match against.
        </p>
      ) : (
        <div className="review-actions">
          <Select
            label={`Who ${report.personName} is at ${report.fundName ?? ''}`}
            value={picked}
            onChange={setPicked}
            placeholder="Match to…"
            options={report.candidates.map((person) => ({
              value: person.id,
              label: person.name,
            }))}
          />
          <button
            className="button small"
            type="button"
            disabled={props.busy || picked === NOBODY}
            onClick={() => props.onDecide({ kind: 'match', investorId: picked })}
          >
            Match
          </button>
          <button
            className="button small"
            type="button"
            disabled={props.busy}
            onClick={() => props.onDecide({ kind: 'createPerson' })}
          >
            Create person
          </button>
          <button
            className="button small"
            type="button"
            disabled={props.busy}
            onClick={() => props.onDecide({ kind: 'ignore' })}
          >
            Ignore
          </button>
        </div>
      )}

      {report.fundId === null && (
        <div className="review-actions">
          <button
            className="button small"
            type="button"
            disabled={props.busy}
            onClick={() => props.onDecide({ kind: 'ignore' })}
          >
            Ignore
          </button>
        </div>
      )}
    </div>
  );
}

function waitingPhrase(count: number): string {
  if (count === 0) return 'nothing is waiting for review';
  return count === 1 ? '1 report waiting for review' : `${count} reports waiting for review`;
}
